import React, { useCallback, useState } from 'react';

import type { SourceRef } from '@captivateiq/assistant/types';
import { QBox, QStack, QText, QTooltip } from '@captivateiq/quotient';

type SourcesFooterProps = {
  sources: SourceRef[];
};

const MAX_COLLAPSED_BADGES = 3;

const truncateUrl = (url: string, maxLength = 40): string => {
  try {
    const parsed = new URL(url);
    const display = parsed.hostname + parsed.pathname;
    if (display.length <= maxLength) return display;
    return `${display.slice(0, maxLength - 1)}\u2026`;
  } catch {
    if (url.length <= maxLength) return url;
    return `${url.slice(0, maxLength - 1)}\u2026`;
  }
};

const badgeSx = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '14px',
  height: '14px',
  fontSize: '9px',
  fontWeight: 600,
  color: 'white',
  backgroundColor: 'grey.500',
  borderRadius: '7px',
  flexShrink: 0,
} as const;

const chipSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  px: 1,
  py: 0.25,
  borderRadius: '12px',
  border: '1px solid',
  borderColor: 'grey.300',
  fontSize: '11px',
  color: 'primary.main',
  cursor: 'pointer',
  backgroundColor: 'white',
  maxWidth: '100%',
  '&:hover': {
    backgroundColor: 'primary.50',
    borderColor: 'primary.main',
  },
} as const;

export const SourcesFooter = ({ sources }: SourcesFooterProps) => {
  const [expanded, setExpanded] = useState(false);

  const handleOpen = useCallback((url: string) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, []);

  if (sources.length === 0) return null;

  if (sources.length === 1) {
    const source = sources[0];
    return (
      <QBox sx={{ borderTop: '1px solid', borderColor: 'grey.200', pt: 1.5, mt: 1 }}>
        <QText sx={{ color: 'grey.500', fontSize: '11px', fontWeight: 600, mb: 0.75 }}>Sources</QText>
        <QTooltip placement="top" title={source.data_context || source.label || source.url}>
          <QBox sx={chipSx} onClick={() => handleOpen(source.url)}>
            <QBox component="span" sx={badgeSx}>
              1
            </QBox>
            <QText
              sx={{
                fontSize: '11px',
                color: 'primary.main',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {source.label || truncateUrl(source.url)}
            </QText>
          </QBox>
        </QTooltip>
      </QBox>
    );
  }

  if (!expanded) {
    const visibleCount = Math.min(sources.length, MAX_COLLAPSED_BADGES);
    const overflow = sources.length - visibleCount;

    return (
      <QBox sx={{ borderTop: '1px solid', borderColor: 'grey.200', pt: 1.5, mt: 1 }}>
        <QBox sx={chipSx} onClick={() => setExpanded(true)}>
          {sources.slice(0, visibleCount).map((source, i) => (
            <QBox component="span" key={`${source.url}:${source.label}`} sx={badgeSx}>
              {i + 1}
            </QBox>
          ))}
          {overflow > 0 && (
            <QText component="span" sx={{ fontSize: '9px', fontWeight: 600, color: 'grey.500' }}>
              +{overflow}
            </QText>
          )}
          <QText component="span" sx={{ fontSize: '11px', color: 'grey.600', fontWeight: 500 }}>
            {sources.length} sources
          </QText>
        </QBox>
      </QBox>
    );
  }

  return (
    <QBox sx={{ borderTop: '1px solid', borderColor: 'grey.200', pt: 1.5, mt: 1 }}>
      <QBox
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0.75,
          cursor: 'pointer',
          '&:hover .collapse-label': { color: 'primary.main' },
        }}
        onClick={() => setExpanded(false)}
      >
        <QText sx={{ color: 'grey.500', fontSize: '11px', fontWeight: 600 }}>Sources</QText>
        <QText className="collapse-label" sx={{ color: 'grey.400', fontSize: '10px', fontWeight: 500 }}>
          Show less
        </QText>
      </QBox>
      <QStack direction="row" spacing="6px" sx={{ flexWrap: 'wrap', gap: '6px' }}>
        {sources.map((source, index) => (
          <QTooltip
            key={`${source.url}:${source.label}`}
            placement="top"
            title={source.data_context || source.label || source.url}
          >
            <QBox sx={chipSx} onClick={() => handleOpen(source.url)}>
              <QBox component="span" sx={badgeSx}>
                {index + 1}
              </QBox>
              <QText
                sx={{
                  fontSize: '11px',
                  color: 'primary.main',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {source.label || truncateUrl(source.url)}
              </QText>
            </QBox>
          </QTooltip>
        ))}
      </QStack>
    </QBox>
  );
};
